import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SupplierService } from 'src/app/views/settings/suppliers/supplier.service';
import {
  ExistingSupplierMatch,
  SupplierCreateChoice,
  existingMatchesFromError,
  hasExactEmailMatch,
} from 'src/app/shared/models/existing-supplier-match.model';
import {
  ExistingSupplierChoiceComponent,
  ExistingSupplierChoiceData,
} from 'src/app/shared/components/existing-supplier-choice/existing-supplier-choice.component';

/** A choice made earlier (the old "validate e-mail" step) for one typed e-mail. */
export interface PresetSupplierChoice extends SupplierCreateChoice {
  forEmail: string;
}

/**
 * "Already on MPS?" step for the supplier create screens without an inline choice of their own (2026-10-20):
 * settings Add Supplier, the RFQ form / line-item "add supplier" forms and the service RFQ add-supplier.
 *
 * run(email, company, call) asks /supplier/existingSupplierMatches first; with matches it shows the choice dialog
 * and passes the buyer's choice (linkSupplierId or createNew) to `call`. A 409 EXISTING_SUPPLIER_MATCHES from the
 * create call (a race) shows the same dialog and retries once; a second 409 shows the server's message. Resolves
 * null when the buyer cancels (or the retry was refused).
 */
@Injectable({ providedIn: 'root' })
export class ExistingSupplierCheckService {
  constructor(private supplierService: SupplierService, private dialog: MatDialog, private snack: MatSnackBar) {}

  /** {} when nothing matched (create as before), the buyer's choice, or null when cancelled. */
  async choose(email: string, companyName: string): Promise<SupplierCreateChoice | null> {
    const matches = await this.supplierService.findExistingSupplierMatches(email, companyName);
    return matches.length ? this.ask(matches) : {};
  }

  async ask(matches: ExistingSupplierMatch[]): Promise<SupplierCreateChoice | null> {
    const data: ExistingSupplierChoiceData = { matches, allowCreateNew: !hasExactEmailMatch(matches) };
    const ref = this.dialog.open(ExistingSupplierChoiceComponent, {
      data,
      width: '560px',
      maxWidth: '95vw',
      autoFocus: false,
      panelClass: 'existing-supplier-choice-panel',
    });
    return (await ref.afterClosed().toPromise()) || null; // rxjs 6 in this app
  }

  /**
   * Runs a create call behind the duplicate check. Resolves null when the buyer cancelled the choice.
   * @param preset a choice the buyer already made for this e-mail (old "validate e-mail" step): no second dialog
   */
  async run<T>(
    email: string,
    companyName: string,
    call: (choice: SupplierCreateChoice) => Promise<T>,
    preset?: PresetSupplierChoice | null
  ): Promise<T | null> {
    const usePreset = !!preset && String(preset.forEmail || '').trim().toLowerCase() === String(email || '').trim().toLowerCase();
    const choice = usePreset ? preset : await this.choose(email, companyName);
    if (!choice) return null;
    try {
      return await call(apiChoice(choice));
    } catch (error) {
      const matches = existingMatchesFromError(error);
      if (!matches || !matches.length) throw error;
      const retry = await this.ask(matches);
      if (!retry) return null;
      try {
        return await call(apiChoice(retry));
      } catch (again) {
        // F5: a second "already on MPS" answer is said, not swallowed (the interceptor skips this 409's snackbar)
        if (existingMatchesFromError(again)) {
          this.snack.open(serverMessage(again) || 'This supplier is already on MPS. Please choose it again.', 'OK', {
            duration: 5000,
            panelClass: 'custom-snack',
          });
          return null;
        }
        throw again;
      }
    }
  }

  /**
   * The old "validate e-mail" step found suppliers the buyer may not edit (another company's account). Instead of
   * the edit form, offer the "Already on MPS" choice: Link calls `linkCall` straight away; "create new" is returned
   * so the screen can carry it to its create call.
   */
  async linkInsteadOfEdit(
    email: string,
    companyName: string,
    linkCall: (choice: SupplierCreateChoice) => Promise<any>
  ): Promise<{ outcome: 'linked' | 'createNew' | 'cancelled' | 'none'; result?: any }> {
    const matches = await this.supplierService.findExistingSupplierMatches(email, companyName);
    if (!matches.length) {
      this.snack.open('This supplier has its own MPS account, so it cannot be edited here.', 'OK', {
        duration: 5000,
        panelClass: 'custom-snack',
      });
      return { outcome: 'none' };
    }
    const choice = await this.ask(matches);
    if (!choice) return { outcome: 'cancelled' };
    if (choice.createNew) return { outcome: 'createNew' };
    try {
      const result = await linkCall(apiChoice(choice));
      this.snack.open(linkedMessage(result, choice.linkedName), 'OK', { duration: 4000, panelClass: 'custom-snack' });
      return { outcome: 'linked', result };
    } catch (error) {
      this.snack.open(serverMessage(error) || 'The supplier could not be linked. Please try again.', 'OK', {
        duration: 5000,
        panelClass: 'custom-snack',
      });
      return { outcome: 'cancelled' };
    }
  }
}

/** Only the fields the create APIs read. */
export function apiChoice(choice: SupplierCreateChoice): SupplierCreateChoice {
  const out: SupplierCreateChoice = {};
  if (choice?.linkSupplierId) out.linkSupplierId = choice.linkSupplierId;
  if (choice?.createNew) out.createNew = true;
  return out;
}

/** What a Link answer means for the user (F5: an admin Link with no buyer writes nothing). */
export function linkedMessage(result: any, name?: string): string {
  if (result && result.mappingCreated === false && result.success !== false) return 'Already on MPS; nothing was created.';
  if (result && result.success === false && result.message) return result.message;
  return `${name ? name + ' is' : 'The existing MPS supplier is'} now in your suppliers; no new account was created.`;
}

function serverMessage(error: any): string {
  return error?.error?.errors?.[0]?.message || '';
}
